import 'bootstrap'
import $ from 'jquery'
import Dropzone from 'dropzone'
import 'dropzone/dist/dropzone.css'

Dropzone.autoDiscover = false

class BlenderFileUploader {
    constructor() {
        this.dropzone = null;
        this.activeUploads = new Map();
        this.statusPollers = new Map();
        this.MAX_RETRIES = 3;
        this.RETRY_DELAY = 2000;
        this.initializeDropzone();
    }

    initializeDropzone() {
        const dropzoneElement = document.getElementById('blender-upload-dropzone');
        if (!dropzoneElement) return;

        this.dropzone = new Dropzone(dropzoneElement, {
            url: API_URL_LINK + '/direct_uploads',
            method: 'post',
            paramName: 'file',
            maxFilesize: 400,
            acceptedFiles: '.blend,.zip',
            parallelUploads: 2,
            timeout: 0,
            chunking: false,
            autoProcessQueue: false,
            previewsContainer: '#blender-upload-previews',
            clickable: [dropzoneElement, document.getElementById('select-files-btn')],
            dictDefaultMessage: '',
            previewTemplate: this.getPreviewTemplate(),
            createImageThumbnails: false,
            headers: this.getHeaders()
        });

        this.setupEventHandlers();
    }

    setupEventHandlers() {
        this.dropzone.on('addedfile', (file) => {
            this.customizeFilePreview(file);
            this.createUploadSession(file);
        });

        this.dropzone.on('removedfile', this.handleFileRemoved.bind(this));
    }

    validFileSize(fileSize) {
        const MAX_SIZE = 400 * 1024 * 1024; // 400MB in bytes
        return fileSize <= MAX_SIZE;
    }

    async createUploadSession(file) {
        if (!this.validFileSize(file.size)) {
            this.handleUploadError(file, 'File too large. Maximum size is 400MB');
            return;
        }

        try {
            const response = await fetch(API_URL_LINK + '/direct_uploads', {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({
                    filename: file.name,
                    file_size: file.size,
                    file_type: file.type
                })
            });

            if (!response.ok) {
                let errorMessage = 'Upload failed';
                let errorData = null;

                try {
                    errorData = await response.json();
                } catch (e) {
                }

                if (errorData && errorData.error) {
                    errorMessage = errorData.error;
                } else if (errorData && errorData.errors && errorData.errors.length > 0) {
                    errorMessage = errorData.errors[0];
                } else {
                    switch (response.status) {
                        case 413:
                        case 422:
                            errorMessage = 'File too large. Maximum size is 400MB';
                            break;
                        case 415:
                            errorMessage = 'Unsupported file format';
                            break;
                        case 401:
                            errorMessage = 'Authentication failed';
                            break;
                        case 500:
                            errorMessage = 'Server error';
                            break;
                        default:
                            errorMessage = `Upload failed (${response.status})`;
                    }
                }

                throw new Error(errorMessage);
            }

            const data = await response.json();

            if (data.success) {
                file.uploadId = data.upload_id;
                file.uploadUrl = data.upload_url;

                this.startChunkedUpload(file, data);
            } else {
                var errorMessage = 'Upload failed';
                if (data.errors && data.errors.length > 0) {
                    errorMessage = data.errors[0];
                }
                this.handleUploadError(file, errorMessage);
            }

        } catch (error) {
            console.error('Upload session error:', error);
            this.handleUploadError(file, error.message);
        }
    }

    async startChunkedUpload(file, uploadResponse) {
        var uploadId = uploadResponse.upload_id;
        var uploadUrl = API_URL_LINK + uploadResponse.upload_url;

        file.uploadId = uploadId;
        file.uploadUrl = uploadUrl;

        this.activeUploads.set(uploadId, {
            file: file,
            status: 'receiving_chunks',
            retryCount: 0
        });

        this.startStatusPolling(uploadId);

        try {
            await this.uploadFileInChunks(file, uploadId, uploadUrl);
        } catch (error) {
            console.error('Chunk upload failed:', error);
            this.handleUploadError(file, 'Upload failed: ' + error.message);
        }
    }

    async uploadFileInChunks(file, uploadId, uploadUrl) {
        var CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
        var totalChunks = Math.ceil(file.size / CHUNK_SIZE);

        for (var chunkNumber = 0; chunkNumber < totalChunks; chunkNumber++) {
            var start = chunkNumber * CHUNK_SIZE;
            var end = Math.min(start + CHUNK_SIZE, file.size);
            var chunk = file.slice(start, end);

            await this.uploadChunkWithRetry(chunk, chunkNumber, totalChunks, uploadId, uploadUrl);
        }
    }

    async uploadChunkWithRetry(chunk, chunkNumber, totalChunks, uploadId, uploadUrl, retryCount) {
        if (retryCount === undefined) retryCount = 0;

        try {
            var formData = new FormData();
            formData.append('file', chunk);
            formData.append('chunk_number', chunkNumber);
            formData.append('total_chunks', totalChunks);

            var response = await fetch(uploadUrl, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-CSRF-Token': this.getCSRFToken()
                }
            });

            if (!response.ok) {
                throw new Error('HTTP ' + response.status);
            }


        } catch (error) {
            if (retryCount < this.MAX_RETRIES) {
                await this.delay(this.RETRY_DELAY * (retryCount + 1));
                return this.uploadChunkWithRetry(chunk, chunkNumber, totalChunks, uploadId, uploadUrl, retryCount + 1);
            }
            throw new Error('Failed to upload chunk ' + chunkNumber + ' after ' + this.MAX_RETRIES + ' attempts: ' + error.message);
        }
    }

    startStatusPolling(uploadId) {
        this.stopStatusPolling(uploadId);

        var poller = setInterval(async () => {
            await this.checkUploadStatus(uploadId);
        }, 2000);

        this.statusPollers.set(uploadId, poller);
    }

    stopStatusPolling(uploadId) {
        var poller = this.statusPollers.get(uploadId);
        if (poller) {
            clearInterval(poller);
            this.statusPollers.delete(uploadId);
        }
    }

    async checkUploadStatus(uploadId) {
        try {
            var response = await fetch(API_URL_LINK + '/uploads/' + uploadId + '/status', {
                method: 'GET',
                headers: this.getHeaders()
            });

            if (!response.ok) {
                if (response.status >= 400 && response.status < 500) {
                    console.error('Client error, stopping polling:', response.status);
                    this.stopStatusPolling(uploadId);
                    return;
                }
                throw new Error('HTTP ' + response.status);
            }

            var data = await response.json();
            this.updateUploadStatus(uploadId, data);

        } catch (error) {
            console.error('Status check error:', error);
        }
    }

    updateUploadStatus(uploadId, statusData) {
        var uploadInfo = this.activeUploads.get(uploadId);
        if (!uploadInfo) return;

        var file = uploadInfo.file;

        this.updateProgress(file, statusData.progress, statusData.status);

        if (statusData.status === 'completed') {
            this.stopStatusPolling(uploadId);
            this.activeUploads.delete(uploadId);

            setTimeout(() => {
                console.log('File completed, showing success preview for:', uploadId);
                this.showSuccessInPreview(file, uploadId);
            }, 500);
        }

        if (statusData.status === 'failed') {
            this.stopStatusPolling(uploadId);
            this.activeUploads.delete(uploadId);

            setTimeout(() => {
                this.showErrorInPreview(file);
            }, 500);
        }
    }

    showSuccessInPreview(file, uploadId) {
        console.log('showSuccessInPreview called for:', uploadId);
        const previewElement = file.previewElement;
        if (!previewElement) {
            console.error('No preview element found');
            return;
        }

        previewElement.classList.add('blender-completed');

        if (file.customData && file.customData.statusLine1 && file.customData.statusLine2) {
            file.customData.statusLine1.innerHTML = `
                <span class="text-success">
                    <i class="fas fa-check-circle me-1"></i>
                    Upload complete
                </span>
            `;
            file.customData.statusLine1.className = 'blender-status-line1 blender-status-completed';

            file.customData.statusLine2.innerHTML = `
                <div class="d-flex justify-content-between align-items-center mt-1">
                    <small class="text-muted">
                        <i class="fas fa-info-circle me-1"></i>
                        Ready for rendering
                    </small>
                    <div class="blender-action-buttons">
                        <a href="/uploads/${uploadId}" class="btn btn-sm btn-outline-secondary me-1">
                            <i class="fas fa-eye me-1"></i>View
                        </a>
                        <a href="/render_jobs/${uploadId}/configure" class="btn btn-sm btn-success">
                            <i class="fas fa-cogs me-1"></i>Render
                        </a>
                    </div>
                </div>
            `;
            file.customData.statusLine2.style.display = 'block';
            file.customData.statusLine2.style.opacity = '1';
        } else {
            console.error('Status elements not found');
        }

        if (file.customData && file.customData.progressElement) {
            file.customData.progressElement.style.width = '100%';
            file.customData.progressElement.classList.remove('bg-primary');
            file.customData.progressElement.classList.add('bg-success');
        }

        if (file.customData && file.customData.progressText) {
            file.customData.progressText.textContent = '100%';
            file.customData.progressText.classList.add('text-success', 'fw-bold');
        }
    }

    showErrorInPreview(file) {
        const previewElement = file.previewElement;
        if (!previewElement) return;

        previewElement.classList.add('blender-error');

        if (file.customData && file.customData.statusLine1 && file.customData.statusLine2) {
            file.customData.statusLine1.innerHTML = `
              <span class="text-danger">
                <i class="fas fa-exclamation-triangle me-1"></i>
                Upload Failed
              </span>
            `;

            file.customData.statusLine2.innerHTML = `
              <div class="d-flex justify-content-between align-items-center mt-1">
                <small class="text-danger">
                  Please try again
                </small>
              </div>
            `;
            file.customData.statusLine2.style.display = 'block';
        }
    }

    customizeFilePreview(file) {
        var previewElement = file.previewElement;
        if (previewElement) {
            var elementsToRemove = previewElement.querySelectorAll('.dz-image-preview, .dz-details, .dz-success-mark, .dz-error-mark');
            elementsToRemove.forEach(function(el) {
                el.remove();
            });

            file.customData = {
                statusLine1: previewElement.querySelector('.blender-status-line1'),
                statusLine2: previewElement.querySelector('.blender-status-line2'),
                progressElement: previewElement.querySelector('.blender-progress-fill'),
                progressText: previewElement.querySelector('.blender-progress-text'),
            };
        }
    }

    updateProgress(file, progress, status) {
        if (!file.customData) return;

        var roundedProgress = Math.round(progress);

        if (file.customData.progressElement) {
            file.customData.progressElement.style.width = progress + '%';
        }

        if (file.customData.progressText) {
            file.customData.progressText.textContent = roundedProgress + '%';
        }

        if (file.customData.statusLine1) {
            if (status !== 'completed') {
                file.customData.statusLine1.textContent = this.getStatusMessage(status, progress);
                file.customData.statusLine1.className = 'blender-status-line1 blender-status-' + status;

                if (file.customData.statusLine2) {
                    file.customData.statusLine2.style.display = 'none';
                }
            }
        }
    }

    handleUploadError(file, errorMessage) {
        console.error('Upload error:', errorMessage);

        if (file.uploadId) {
            this.stopStatusPolling(file.uploadId);
            this.activeUploads.delete(file.uploadId);
        }

        this.updateFileStatus(file, 'error', errorMessage);
    }

    updateFileStatus(file, status, message) {
        if (!file.customData || !file.customData.statusLine1) return;

        file.customData.statusLine1.textContent = message || this.getStatusMessage(status);
        file.customData.statusLine1.className = 'blender-status-line1 blender-status-' + status;

        var previewElement = file.previewElement;
        if (previewElement) {
            var statusClasses = ['blender-completed', 'blender-error', 'blender-processing', 'blender-uploading'];
            statusClasses.forEach(function(className) {
                previewElement.classList.remove(className);
            });

            if (status === 'completed') previewElement.classList.add('blender-completed');
            else if (status === 'error' || status === 'failed') previewElement.classList.add('blender-error');
            else if (status === 'processing') previewElement.classList.add('blender-processing');
            else if (status === 'uploading') previewElement.classList.add('blender-uploading');
        }
    }

    handleFileRemoved(file) {
        if (file.uploadId) {
            this.stopStatusPolling(file.uploadId);
            this.activeUploads.delete(file.uploadId);
        }
    }

    getHeaders() {
        return {
            'X-AUTH-TOKEN': AUTH_TOKEN,
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCSRFToken()
        };
    }

    getCSRFToken() {
        var metaTag = document.querySelector('meta[name="csrf-token"]');
        return metaTag ? metaTag.getAttribute('content') : '';
    }

    delay(ms) {
        return new Promise(function(resolve) {
            setTimeout(resolve, ms);
        });
    }

    getPreviewTemplate() {
        return '<div class="blender-file-preview" data-dz-preview>' +
            '<div class="blender-file-info">' +
            '<div class="blender-filename" data-dz-name></div>' +
            '<div class="blender-filesize" data-dz-size></div>' +
            '</div>' +
            '<div class="blender-progress-container">' +
            '<div class="blender-progress-bar">' +
            '<div class="blender-progress-fill" data-dz-uploadprogress></div>' +
            '</div>' +
            '<div class="blender-progress-text">0%</div>' +
            '</div>' +
            '<div class="blender-status-container">' +
            '<div class="blender-status-line1">Waiting</div>' +
            '<div class="blender-status-line2" style="display: none;"></div>' +
            '</div>' +
            '<div class="blender-actions">' +
            '<button class="blender-cancel-btn" data-dz-remove>×</button>' +
            '</div>' +
            '</div>';
    }

    getStatusMessage(status, progress) {
        if (progress === undefined) progress = 0;

        var messages = {
            'pending': 'Waiting to start',
            'receiving_chunks': 'Receiving chunks... ' + Math.round(progress) + '%',
            'assembling': 'Assembling file... ' + Math.round(progress) + '%',
            'validating': 'Validating file... ' + Math.round(progress) + '%',
            'completed': 'Completed successfully',
            'failed': 'Processing failed',
            'error': 'Error occurred'
        };

        return messages[status] || status + '... ' + Math.round(progress) + '%';
    }

    destroy() {
        var _this = this;
        this.statusPollers.forEach(function(poller, uploadId) {
            clearInterval(poller);
        });
        this.statusPollers.clear();
        this.activeUploads.clear();

        if (this.dropzone) {
            this.dropzone.destroy();
        }
    }
}

$(document).on('turbolinks:load', function() {
    if (document.getElementById('blender-upload-dropzone')) {
        window.blenderUploader = new BlenderFileUploader();
    }
});

$(document).on('turbolinks:before-render', function() {
    if (window.blenderUploader) {
        window.blenderUploader.destroy();
        window.blenderUploader = null;
    }
});
